import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Card, Col, Modal, Popover, Row, Spin, Tag } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import moment from "moment";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import {
  EyeOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  ReconciliationOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  getSingleTransaction,
  ChangeTransactionStatus,
  completeTransaction,
} from "../../../network/network";
import {
  AddonName,
  TransactionStatus,
  TransactionType,
  TransactionOrderSource,
  KitchenDisplayStatus,
  OrderType,
  UserType,
} from "../../../configs/constants";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import ReactToPrint from "react-to-print";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import UpdateWaiter from "./UpdateWaiter";
import UpdateTable from "./UpdateTable";
import TransactionSlip from "../../../components/TransactionSlip/TransactionSlip";
import TaxCalculator from "../../../components/TaxCalculator";
import PrintOrder from "../../../components/TransactionSlip/PrintOrder";
import {
  getEntityBranchId,
  getOrderBranchId,
} from "../../../utils/branchScope";
import Checkout from "../Checkout/Checkout";
import { handleApiError } from "../../../utils/errorHandler";

const ViewOrder = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const [data, setData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [transaction, setTransaction] = useState({});
  const [slipModalVisible, setSlipModalVisible] = useState(false);
  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
  const [checkoutData, setCheckoutData] = useState(null);
  const [modal, contextHolder] = Modal.useModal();

  let { orderId } = useParams();
  const { t } = useTranslation();
  const completedSlipRef = useRef();
  const orderBillPrintRef = useRef(null);
  let navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    getSingleTransaction(orderId)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setData(response.data.data.transaction);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
      });
  }, [orderId, reload, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/orders",
      breadcrumbName: t("orders"),
      icon: <ReconciliationOutlined />,
    },
    {
      path: "view-orders",
      breadcrumbName: t("view_order"),
      icon: <EyeOutlined />,
    },
  ];

  const updateStatus = (id, status) => {
    if (status === TransactionStatus.Completed) {
      setCheckoutData({
        diningTableId: data.dining_table_id,
        waiterId: data.waiter && data.waiter.id,
        waiterBranchId: getEntityBranchId(data.waiter),
        branchId: getOrderBranchId(data, getEntityBranchId(authUser)),
        items: data.items,
        orderStatus: TransactionStatus.Completed,
        id: data.id,
      });
      setCheckoutModalVisible(true);
    } else {
      setLoading(true);
      const formData = new FormData();

      if (status) {
        formData.append("id", id);
        formData.append("status", status);
      }

      ChangeTransactionStatus(formData)
        .then((response) => {
          let responseData = response.data || response.response.data;

          if (responseData.success === true) {
            NotificationWithIcon(
              "success",
              t("order_updated"),
              t("order_updated_successfully"),
            );
            navigate("/orders");
          } else if (responseData?.success === false) {
            handleApiError(response, t);
          }
        })
        .catch((error) => handleApiError(error, t))
        .finally(() => setLoading(false));
    }
  };

  const completePaidOrder = (id) => {
    setLoading(true);
    completeTransaction(id)
      .then((response) => {
        let responseData = response.data || response.response.data;

        if (responseData.success === true) {
          NotificationWithIcon(
            "success",
            t("order_updated"),
            t("order_updated_successfully"),
          );
          navigate("/orders");
        } else if (responseData?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => setLoading(false));
  };

  const showSlipModal = (transaction) => {
    setTransaction(transaction);
    setSlipModalVisible(true);
  };

  const hideSlipModal = () => {
    setSlipModalVisible(false);
    setTransaction({});
  };

  const closeCheckoutModal = () => {
    setCheckoutModalVisible(false);
    setCheckoutData(null);
    setReload(!reload);
  };

  const handleCheckoutDone = () => {
    setReload(!reload);
  };

  const getTransactionsType = (type) => {
    if (type === TransactionType.Cash) {
      return <Tag color="green">{t("cash")}</Tag>;
    } else if (type === TransactionType.Credit) {
      return <Tag color="blue">{t("credit_card")}</Tag>;
    } else if (type === TransactionType.Multipayment) {
      return <Tag color="gold">{t("multi_payment")}</Tag>;
    }
    return "-";
  };

  const is_waiter_management_active = authUser.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.WaiterManagement,
  );

  const is_job_management_active = authUser.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.JobManagement,
  );

  const is_table_management_active = authUser.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.TableManagement,
  );

  const is_kitchen_display_active = authUser.active_addons?.some(
    (addon) => addon.id && addon.name === AddonName.KitchenDisplaySystem,
  );

  const is_takeaway_order =
    String(data?.order_type?.value || "").toLowerCase() ===
      OrderType.TakeAway ||
    String(data?.order_type?.name || "").toLowerCase() ===
      OrderType.TakeAway;

  const has_kitchen_display_status =
    data?.kitchen_display_status?.value !== undefined &&
    data?.kitchen_display_status?.value !== null;
  const kitchen_display_status_value = Number(
    data?.kitchen_display_status?.value,
  );
  const should_apply_kitchen_display_rules =
    is_kitchen_display_active && has_kitchen_display_status;

  const is_edit_order_disabled =
    is_takeaway_order ||
    (should_apply_kitchen_display_rules &&
      data?.status?.value === TransactionStatus.InProgress &&
      kitchen_display_status_value !== KitchenDisplayStatus.Awaiting);

  const tax = Number(data?.tax ?? 0);
  const amountCharged = Number(data?.amount_charged ?? 0);
  const discountAmount = Number(data?.discount_amount ?? 0);
  const orderSubTotal = Number(
    data?.sub_total ?? amountCharged - tax + discountAmount,
  );
  const hasDiscount = Boolean(
    data?.discount && discountAmount > 0 && !data?.reference_transaction,
  );

  const isVatExempt =
    authUser?.is_vat_exempt === 1 || authUser?.is_vat_exempt === true;

  const handleCancelOrder = () => {
    if (is_takeaway_order) {
      modal.confirm({
        title: t("cancel_order"),
        icon: (
          <ExclamationCircleOutlined
            style={{ color: "var(--color-warning)" }}
          />
        ),
        content: t("takeaway_cancel_refund_warning"),
        okText: t("cancel_order"),
        cancelText: t("back"),
        okButtonProps: {
          type: "text",
          danger: true,
        },
        centered: true,
        onOk: () => {
          updateStatus(data.id, TransactionStatus.Cancelled);
        },
      });
      return;
    }

    updateStatus(data.id, TransactionStatus.Cancelled);
  };

  return (
    <div className="app-relative">
      {contextHolder}

      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("view_order")}
          onBack={() => window.history.back()}
          ghost={false}
          className="app-p-0"
        />
      </Card>

      <Spin spinning={loading}>
        <Card
          style={{
            direction: appConfig.direction
              ? appConfig.direction === "ltr"
                ? "ltr"
                : "rtl"
              : "ltr",
          }}
        >
          {data && (
            <Row gutter={[20, 20]}>
              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <div className="app-detail-label">
                  {t("transaction_id")}
                </div>
                <div
                  className="app-detail-value"
                >
                  {data.uid ? data.uid : "-"}
                </div>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <div className="app-detail-label">
                  {t("order_id")}
                </div>
                <div
                  className="app-detail-value"
                >
                  {data.order_id ? data.order_id : "-"}
                </div>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <div className="app-detail-label">
                  {t("status")}
                </div>
                <div
                  className="app-detail-value"
                >
                  {data.status.value === TransactionStatus.Pending && (
                    <Tag color="warning">{data.status.name}</Tag>
                  )}
                  {data.status.value === TransactionStatus.InProgress && (
                    <Tag color="blue">{data.status.name}</Tag>
                  )}
                  {data.status.value === TransactionStatus.Completed && (
                    <Tag color="green">{data.status.name}</Tag>
                  )}
                  {data.status.value === TransactionStatus.Cancelled && (
                    <Tag color="red">{data.status.name}</Tag>
                  )}
                </div>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                <div className="app-detail-label">
                  {t("source")}
                </div>
                <div
                  className="app-bordered-inline"
                >
                  {data.order_source ? (
                    <div
                      className="app-flex-wrap-center"
                    >
                      {Number(data.order_source?.value) ===
                      TransactionOrderSource.Pos ? (
                        <img
                          src="/icons/pos.svg"
                          alt={t("pos")}
                          style={{
                            width: "25px",
                            height: "auto",
                            marginRight: "10px",
                          }}
                        />
                      ) : Number(data.order_source?.value) ===
                        TransactionOrderSource.QrOrder ? (
                        data.is_drive_thru ? (
                          <img
                            src="/icons/car.svg"
                            alt={t("drive_thru")}
                            style={{
                              width: "25px",
                              height: "auto",
                              marginRight: "10px",
                            }}
                          />
                        ) : (
                          <img
                            src="/icons/qr.svg"
                            alt={t("qr")}
                            style={{
                              width: "25px",
                              height: "auto",
                              marginRight: "10px",
                            }}
                          />
                        )
                      ) : Number(data.order_source?.value) ===
                        TransactionOrderSource.Kiosk ? (
                        <img
                          src="/icons/kiosk.svg"
                          alt={t("kiosk")}
                          style={{
                            width: "25px",
                            height: "auto",
                            marginRight: "10px",
                          }}
                        />
                      ) : null}
                      {data.order_source.name}
                    </div>
                  ) : (
                    "-"
                  )}
                </div>
              </Col>

              {data.dining_table_name && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("dining_table")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {data.dining_table_name}
                  </div>
                </Col>
              )}

              {authUser.type === UserType.BusinessOwner && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("branch")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {data.branch ? data.branch : "-"}
                  </div>
                </Col>
              )}

              {data.created_at && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("created_at")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {moment(data?.created_at).format("MMM. D, YYYY hh:mm a")}
                  </div>
                </Col>
              )}

              {data.user && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("created_by")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {data.user}
                  </div>
                </Col>
              )}

              {data.waiter &&
                (is_waiter_management_active || is_job_management_active) && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                    <div className="app-detail-label">
                      {is_waiter_management_active
                        ? t("waiter")
                        : t("job_person")}
                    </div>
                    <div
                      className="app-detail-value"
                    >
                      {data.waiter?.name}
                    </div>
                  </Col>
                )}

              {data.type &&
                data.status.value === TransactionStatus.Completed && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                    <div className="app-detail-label">
                      {t("type")}
                    </div>
                    <div
                      className="app-detail-value"
                    >
                      {getTransactionsType(data.type)}
                    </div>
                  </Col>
                )}

              {data.status.value === TransactionStatus.Completed && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("status")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {data.reference_transaction ? (
                      <>
                        <Tag color="error">{t("status_refunded")}</Tag>
                        <br />
                        {t("reference_no")}:{" "}
                        <strong>{data.reference_transaction.uid}</strong>
                      </>
                    ) : (
                      <Tag color="success">{t("status_paid")}</Tag>
                    )}
                  </div>
                </Col>
              )}

              {data.customer_name && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("customer_name")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {data.customer_name}
                  </div>
                </Col>
              )}

              {data.vehicle_color && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("vehicle_color")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {data.vehicle_color}
                  </div>
                </Col>
              )}

              {data.vehicle_number && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("vehicle_number")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {data.vehicle_number}
                  </div>
                </Col>
              )}

              {data.order_type && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("order_type")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    <Tag color="pink">{data.order_type?.name}</Tag>
                  </div>
                </Col>
              )}

              {is_kitchen_display_active && data.kitchen_display_status && (
                <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={8}>
                  <div className="app-detail-label">
                    {t("kitchen_status")}
                  </div>
                  <div
                    className="app-detail-value"
                  >
                    {(() => {
                      const statusMap = {
                        1: { text: t("awaiting"), color: "orange" },
                        2: { text: t("preparing"), color: "var(--color-info)" },
                        3: { text: t("cooked"), color: "var(--color-success)" },
                      };

                      const status =
                        statusMap[data.kitchen_display_status?.value];

                      if (!status) return "-";

                      return <Tag color={status.color}>{status.text}</Tag>;
                    })()}
                  </div>
                </Col>
              )}

              <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                <div
                  className="app-detail-value"
                >
                  {data.items && (
                    <>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: "1px dashed var(--color-text-muted)",
                          padding: "10px 0",
                        }}
                      >
                        <span className="app-font-bold">
                          {t("items")}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontWeight: "bold",
                          }}
                        >
                          <span className="app-w-100px app-text-center">
                            {t("unit_price")}
                          </span>
                          <span
                            style={{
                              width: "250px",
                              textAlign:
                                appConfig.direction === "ltr"
                                  ? "right"
                                  : "left",
                            }}
                          >
                            {t("price_including_tax")}
                          </span>
                        </div>
                      </div>

                      <div
                        style={{
                          paddingBottom: "10px",
                          borderBottom: "1px solid var(--color-text-muted)",
                        }}
                      >
                        {data.items?.map((item) => (
                          <div
                            key={"product_items_" + item.id}
                            className="app-summary-row"
                          >
                            <span>
                              {appConfig.direction === "ltr" ? (
                                <>
                                  {item.quantity} x <b>{item.name_en}</b>
                                </>
                              ) : (
                                <>
                                  {item.quantity} x <b>{item.name}</b>
                                </>
                              )}

                              {item.tags && item.tags.length > 0 && (
                                <div style={{ marginTop: 4 }}>
                                  {item.tags.map((tag) => (
                                    <Tag
                                      key={`${item.id}_${tag.id}`}
                                      color="blue"
                                    >
                                      {appConfig.direction === "ltr"
                                        ? tag.name
                                        : tag.name_ar}
                                    </Tag>
                                  ))}
                                </div>
                              )}

                              {authUser.is_calories && item.calories_info && (
                                <Tag style={{ marginTop: 4 }} color="geekblue">
                                  {t("calories")}: {item.calories_info}
                                </Tag>
                              )}
                            </span>

                            <div
                              className="app-flex-between-center"
                            >
                              <span
                                className="app-w-100px app-text-center"
                              >
                                <CurrencyFormatter
                                  value={item.price}
                                  currencyCode={false}
                                />
                              </span>
                              <span
                                style={{
                                  width: "250px",
                                  textAlign:
                                    appConfig.direction === "ltr"
                                      ? "right"
                                      : "left",
                                }}
                              >
                                <CurrencyFormatter
                                  value={item.subtotal}
                                  currencyCode={false}
                                />
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {hasDiscount && (
                        <>
                          <div
                            className="app-summary-row"
                          >
                            {t("subtotal")}
                            <span>
                              <CurrencyFormatter value={orderSubTotal} />
                            </span>
                          </div>

                          <div
                            className="app-summary-row"
                          >
                            <span>
                              {t("discount")}
                              {data.discount?.discount_percentage
                                ? ` (${data.discount.discount_percentage}%)`
                                : ""}
                            </span>
                            <span>
                              <CurrencyFormatter value={discountAmount} />
                            </span>
                          </div>
                        </>
                      )}

                      <div
                        className="app-summary-row"
                      >
                        {t("total_without_tax")}
                        <span>
                          <CurrencyFormatter value={amountCharged - tax} />
                        </span>
                      </div>

                      {tax > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 0",
                            borderBottom: "1px solid var(--color-text-muted)",
                          }}
                        >
                          <div>
                            {t("total_taxes")}
                            <Popover
                              content={<TaxCalculator data={data} />}
                              trigger="click"
                            >
                              <InfoCircleOutlined
                                className="app-info-icon"
                              />
                            </Popover>
                          </div>
                          <span>
                            <CurrencyFormatter value={tax} />
                          </span>
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 0",
                          borderBottom: "1px solid var(--color-text-muted)",
                        }}
                      >
                        {isVatExempt ? t("total") : t("total_with_tax")}
                        <b>
                          <CurrencyFormatter value={amountCharged} />
                        </b>
                      </div>
                    </>
                  )}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      alignItems: "center",
                      padding: "10px 0",
                    }}
                  >
                    {(authUser.type === UserType.BusinessOwner ||
                      authUser.name === data.user ||
                      authUser.name === data.waiter?.name) &&
                      data.status.value !== TransactionStatus.Cancelled &&
                      data.status.value !== TransactionStatus.Completed && (
                        <>
                          {data.status.value === TransactionStatus.Pending && (
                            <Button
                              type="primary"
                              onClick={() =>
                                updateStatus(
                                  data.id,
                                  TransactionStatus.InProgress,
                                )
                              }
                            >
                              {t("mark_as_in_progress")}
                            </Button>
                          )}

                          {data.status.value ===
                            TransactionStatus.InProgress && (
                            <Button
                              type="primary"
                              disabled={
                                should_apply_kitchen_display_rules &&
                                kitchen_display_status_value !==
                                  KitchenDisplayStatus.Cooked
                              }
                              onClick={() =>
                                data.is_paid === true
                                  ? completePaidOrder(data.id)
                                  : updateStatus(
                                      data.id,
                                      TransactionStatus.Completed,
                                    )
                              }
                            >
                              {t("mark_as_complete")}
                            </Button>
                          )}

                          <Button
                            type="text"
                            danger
                            onClick={handleCancelOrder}
                          >
                            {t("cancel_order")}
                          </Button>

                          {is_table_management_active && (
                            <UpdateTable
                              order={data}
                              reloadCallback={() => setReload(!reload)}
                            />
                          )}

                          {(is_waiter_management_active ||
                            is_job_management_active) &&
                            !authUser.is_waiter && (
                              <UpdateWaiter
                                order={data}
                                reloadCallback={() => setReload(!reload)}
                              />
                            )}

                          <Button
                            type="primary"
                            disabled={is_edit_order_disabled}
                            onClick={() => {
                              if (is_edit_order_disabled) return;

                              navigate("/orders/" + data.id + "/edit", {
                                state: data,
                              });
                            }}
                          >
                            {t("edit_order")}
                          </Button>
                        </>
                      )}

                    {data.status.value === TransactionStatus.Completed ? (
                      <Button
                        type="primary"
                        onClick={() => showSlipModal(data)}
                      >
                        {t("view_invoice")}
                      </Button>
                    ) : data.status.value === TransactionStatus.InProgress ? (
                      <ReactToPrint
                        trigger={() => (
                          <Button key="print" type="primary">
                            {t("print_bill")}
                          </Button>
                        )}
                        content={() => orderBillPrintRef.current}
                        documentTitle={data.order_id}
                      />
                    ) : null}
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Card>

        {data && (
          <div
            style={{
              margin: "20px",
              width: "100%",
              position: "fixed",
              top: -10000,
              left: -10000,
              zIndex: -1,
              direction: appConfig.direction
                ? appConfig.direction === "ltr"
                  ? "ltr"
                  : "rtl"
                : "ltr",
            }}
          >
            <PrintOrder ref={orderBillPrintRef} order={data} />
          </div>
        )}
      </Spin>

      <Modal
        open={checkoutModalVisible}
        onCancel={closeCheckoutModal}
        footer={null}
        width="100vw"
        closable={false}
        maskClosable={false}
        style={{
          top: 0,
          padding: 0,
          margin: 0,
          maxWidth: "100vw",
          height: "100vh",
        }}
        styles={{
          body: { padding: "0", minHeight: "calc(100vh - 40px)" },
        }}
        destroyOnClose
      >
        <Checkout
          injectedState={checkoutData}
          onDone={handleCheckoutDone}
          onBack={closeCheckoutModal}
        />
      </Modal>

      <Modal
        open={slipModalVisible}
        title={t("transaction_id") + " # " + transaction.uid}
        onCancel={hideSlipModal}
        footer={[
          <ReactToPrint
            trigger={() => (
              <Button key="print" type="primary">
                {t("print")}
              </Button>
            )}
            content={() => completedSlipRef.current}
            documentTitle={transaction.uid}
            key="1"
          />,
          <Button type="default" key="back" onClick={hideSlipModal}>
            {t("back")}
          </Button>,
        ]}
      >
        <TransactionSlip
          ref={completedSlipRef}
          transaction={transaction}
          shop={{
            name: authUser.business_name,
            vat: authUser.vat,
            logo: authUser.logo,
            is_vat_exempt: isVatExempt,
          }}
        />
      </Modal>
    </div>
  );
};

export default ViewOrder;
